import * as React from 'react';
import { useFormContext } from 'react-hook-form';

import { formatDate, StatusField, Switch, TextField } from '@amfintech/react-admin-ui';
import { createHelpers, createResource, Form, sentenceCase } from '@amfintech/refine-react-admin';

import {
  Fund,
  FundCategories,
  FundManagers,
  FundRiskProfile,
  FundStatus,
  Maybe,
  ModificationEntityType,
} from '~/api';
import { FundDocumentTypes } from '~/common/enums';

import { resourceNames } from './resource-names';

const formatFundRiskProfile = (riskProfile: FundRiskProfile) => {
  switch (riskProfile) {
    case FundRiskProfile.Low:
      return 'Low';
    case FundRiskProfile.LowMedium:
      return 'Low to Medium';
    case FundRiskProfile.Medium:
      return 'Medium';
    case FundRiskProfile.MediumHigh:
      return 'Medium to High';
    case FundRiskProfile.High:
      return 'High';
    default:
      return 'Unknown';
  }
};

const formatFundManager = (manager: Maybe<FundManagers>) => {
  switch (manager) {
    case FundManagers.Afm:
      return 'AmFunds Management Berhad';
    case FundManagers.Aifm:
      return 'AmIslamic Funds Management Berhad';
    default:
      return 'Unknown';
  }
};

const { defineFields, defineShowPage, defineCardSection, defineRelatedResourceSection } =
  createHelpers<Fund>({
    resourceName: resourceNames.fund,
  });

export const formatFundInfoStatus = (status: string) => {
  switch (status) {
    case 'Active':
      return 'success';
    case 'Inactive':
      return 'danger';
    default:
      return 'pending';
  }
};

export const fundInfoResource = createResource({
  name: resourceNames.fund,
  label: 'Fund Info',
  confirmBeforeSubmit: true,
  confirmationTitle: 'Fund Info Confirmation',
  confirmationDescription: 'Please confirm that you want to create this fund information.',
  confirmBeforeEdit: true,
  editConfirmationTitle: 'Fund Info Update Confirmation',
  editConfirmationDescription: 'Please confirm that you want to update this fund information.',
  defaultValues: {
    fundName: '',
    fundCode: '',
    isin: '',
    rm1FundIndicator: false,
    fundSize: 0,
    totalUnitInCirculation: 0,
  },
  fields: defineFields([
    'id',
    'fundName',
    'fundCode',
    'isin',
    'currencyCode',
    'shariahFlag',
    'esgFlag',
    'localFlag',
    {
      navPrice: ['price', 'date'],
    },
    'collectionBankAccountId',
    'salesChargeDisplay',
    'switchingFeeDisplay',
    'annualManagementFeeDisplay',
    'trusteeFeeDisplay',
    {
      fundPolicy: [
        'id',
        'minInitInvestment',
        'minSubsequentInvestment',
        'minRedemptionUnit',
        'minHoldingUnit',
        'minSwitchingUnit',
        'wholeSaleFlag',
        'salesChargePercent',
        'annualManagementFee',
        'annualTrusteeFee',
        'cutOffTime',
        'redemptionCutOffTime',
      ],
    },
    { fundPrices: ['price', 'date'] },
    { collectionBankAccount: ['beneficiaryName'] },
    {
      trustee: [
        'name',
        'contactNo',
        'faxNo',
        'website',
        'email',
        'registeredAddress',
        'businessAddress',
      ],
    },
    'launchDate',
    'launchPrice',
    'financialYearEnd',
    'fundFocusFlag',
    'rm1FundIndicator',
    'manager',
    'fundSize',
    'fundSizeCurrency',
    'fundSizeDate',
    { fundDocuments: ['id', 'displayName', 'documentLink'] },
    'totalUnitInCirculation',
    'totalUnitInCirculationDate',
    'annualExpenseRatio',
    'annualExpenseRatioDate',
    'fundRiskProfile',
    'pricingBasic',
    'distributionPolicy',
    'investmentObj',
    'status',
    'trusteeId',
    'pricingBasic',
    'category',
    'lipperMyClass',
    'benchmark',
    'switchFee',
    'assetAllocation',
    'createdAt',
    'updatedAt',
  ]),
  allowDownload: true,
  list: {
    filters: {
      collapsible: 'expand-by-default',
    },
    makerChecker: {
      entityTypes: [ModificationEntityType.Fund],
    },
    downloadConfig: {
      category: (value: string) => {
        return value
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      },
      'navPrice.price': (navPrice: any) => {
        return Number(navPrice).toLocaleString('en-MY', {
          minimumFractionDigits: 4,
          maximumFractionDigits: 4,
        });
      },
      'navPrice.date': (date: Date) => {
        return formatDate(date, { formatType: 'dateOnly' });
      },
      filenamePrefix: 'FundInfo',
    },
  },
  controls: {
    sections: [
      {
        title: 'Basic Fund Information',
        items: [
          'fundName',
          'fundCode',
          'isin',
          'fundPolicy.wholeSaleFlag',
          'currencyCode',
          'launchDate',
          'launchPrice',
          'category',
          'fundFocusFlag',
          'rm1FundIndicator',
          'localFlag',
          'shariahFlag',
          'esgFlag',
          'manager',
          'status',
        ],
      },
      {
        title: 'Investment Requirement',
        items: [
          'fundPolicy.minInitInvestment',
          'fundPolicy.minSubsequentInvestment',
          'fundPolicy.minRedemptionUnit',
          'fundPolicy.minHoldingUnit',
          'fundPolicy.minSwitchingUnit',
        ],
      },
      {
        title: 'Operational Details',
        items: [
          'fundPolicy.cutOffTime',
          'fundPolicy.redemptionCutOffTime',
          'financialYearEnd',
          'distributionPolicy',
          'pricingBasic',
        ],
      },
      {
        title: 'Risk and Performance',
        items: ['fundRiskProfile', 'benchmark', 'lipperMyClass', 'assetAllocation'],
      },
      {
        title: 'Objective and Policies',
        items: ['investmentObj'],
      },
      {
        title: 'Trustee Information',
        items: ['trusteeId'],
      },
      {
        title: 'Fees and Charges',
        items: [
          'fundPolicy.salesChargePercent',
          'salesChargeDisplay',
          'fundPolicy.annualManagementFee',
          'annualManagementFeeDisplay',
          'fundPolicy.annualTrusteeFee',
          'trusteeFeeDisplay',
          'switchFee',
          'switchingFeeDisplay',
          'collectionBankAccountId',
        ],
      },
      {
        title: 'Others',
        items: [
          'fundSize',
          'fundSizeCurrency',
          'fundSizeDate',
          'totalUnitInCirculation',
          'totalUnitInCirculationDate',
          'annualExpenseRatio',
          'annualExpenseRatioDate',
        ],
      },
      {
        title: 'Fund Documents',
        items: ['fundDocuments'],
      },
    ],
    components: ({ action }) => ({
      fundName: {
        type: 'text',
        config: {
          label: 'Fund Name',
          required: true,
          maxLength: 191,
        },
      },
      fundCode: {
        type: 'text',
        config: {
          label: 'Fund Code',
          required: true,
          disabled: action === 'edit',
          maxLength: 184,
        },
      },
      isin: {
        type: 'text',
        config: {
          label: 'ISIN Number',
          maxLength: 191,
        },
      },
      'fundPolicy.wholeSaleFlag': {
        type: 'switch',
        config: {
          label: 'Wholesale Indicator',
          required: true,
        },
      },
      currencyCode: {
        type: 'text',
        config: {
          label: 'Currency Code',
          required: true,
          maxLength: 3,
        },
      },
      launchDate: {
        type: 'date',
        config: {
          label: 'Launch Date',
          required: true,
        },
      },
      launchPrice: {
        type: 'number',
        config: {
          label: 'Launch Price',
          required: true,
        },
      },
      category: {
        type: 'select',
        config: {
          label: 'Fund Category',
          required: true,
          options: Object.values(FundCategories)
            .filter((category) => category !== FundCategories.Unknown)
            .map((category) => ({ label: category, value: category })),
        },
      },
      fundFocusFlag: {
        type: 'switch',
        config: {
          label: 'Fund Focus Indicator',
          required: true,
        },
      },
      rm1FundIndicator: {
        type: 'switch',
        config: {
          label: 'RM1 Fund Indicator',
          required: false,
        },
      },
      localFlag: {
        type: 'select',
        config: {
          label: 'Geographical Location',
          valueType: 'boolean',
          required: true,
          options: [
            { label: 'Local', value: 'true' },
            { label: 'Global', value: 'false' },
          ],
        },
      },
      shariahFlag: {
        type: 'switch',
        config: {
          label: 'Shariah',
          required: true,
        },
      },
      esgFlag: {
        type: 'switch',
        config: {
          label: 'ESG',
          required: true,
        },
      },
      manager: {
        type: 'select',
        config: {
          label: 'Manager',
          required: true,
          options: Object.values(FundManagers).map((manager) => ({
            label: formatFundManager(manager),
            value: manager,
          })),
        },
      },
      status: {
        type: 'select',
        config: {
          label: 'Status',
          required: true,
          options: Object.values(FundStatus).map((status) => ({
            label: status,
            value: status,
          })),
        },
      },
      'fundPolicy.minInitInvestment': {
        type: 'number',
        config: {
          label: 'Minimum Initial Investment',
          required: true,
          step: 0.01,
          min: 0,
          max: 999999999999999,
        },
      },
      'fundPolicy.minSubsequentInvestment': {
        type: 'number',
        config: {
          label: 'Minimum Subsequent Investment',
          required: true,
          step: 0.01,
          min: 0,
          max: 999999999999999,
        },
      },
      'fundPolicy.minRedemptionUnit': {
        type: 'number',
        config: {
          label: 'Minimum Redemption Unit',
          required: true,
          step: 0.01,
          min: 0,
          max: 999999999999999,
        },
      },
      'fundPolicy.minHoldingUnit': {
        type: 'number',
        config: {
          label: 'Minimum Holding Unit',
          required: true,
          step: 0.01,
          min: 0,
          max: 999999999999999,
        },
      },
      'fundPolicy.minSwitchingUnit': {
        type: 'number',
        config: {
          label: 'Minimum Switching Unit',
          required: true,
          step: 0.01,
          min: 0,
          max: 999999999999999,
        },
      },
      'fundPolicy.cutOffTime': {
        type: 'text',
        config: {
          label: 'Cut Off Time',
          required: true,
          pattern: '^(0?[1-9]|1[0-2])[.:]([0-5]\\d)(am|pm)?$',
          patternMessage: 'Please enter a valid time in the format HH:MM am/pm, eg. 11:00am',
          maxLength: 191,
        },
      },
      'fundPolicy.redemptionCutOffTime': {
        type: 'text',
        config: {
          label: 'Redemption Cut Off Time',
          required: true,
          pattern: '^(0?[1-9]|1[0-2])[.:]([0-5]\\d)(am|pm)?$',
          patternMessage: 'Please enter a valid time in the format HH:MM am/pm, eg. 11:00am',
          maxLength: 191,
        },
      },
      financialYearEnd: {
        type: 'text',
        config: {
          label: 'Financial Year End',
          required: false,
          maxLength: 191,
        },
      },
      distributionPolicy: {
        type: 'text',
        config: {
          label: 'Distribution Type / Policy',
          required: true,
        },
      },
      pricingBasic: {
        type: 'text',
        config: {
          label: 'Pricing Basic',
          required: true,
          maxLength: 191,
        },
      },
      fundRiskProfile: {
        type: 'select',
        config: {
          label: 'Fund Risk Profile',
          required: true,
          options: Object.values(FundRiskProfile).map((riskProfile) => ({
            label: formatFundRiskProfile(riskProfile),
            value: riskProfile,
          })),
        },
      },
      benchmark: {
        type: 'text',
        config: {
          label: 'Benchmark',
          required: true,
        },
      },
      lipperMyClass: {
        type: 'text',
        config: {
          label: 'Lipper Malaysian Classification',
          required: true,
          maxLength: 191,
        },
      },
      assetAllocation: {
        type: 'text',
        config: {
          label: 'Asset Allocation',
          required: true,
        },
      },
      investmentObj: {
        type: 'textarea',
        config: {
          label: 'Investment Objective',
          required: true,
        },
      },
      trusteeId: {
        type: 'resource-select',
        config: {
          label: 'Trustee Name',
          resourceName: resourceNames.trustee,
          optionLabel: 'name',
          optionValue: 'id',
          fields: ['id', 'name'],
          extendedFields: [
            { label: 'Contact Number', name: 'contactNo' },
            { label: 'Fax Number', name: 'faxNo' },
            { label: 'Website', name: 'website' },
            { label: 'Email', name: 'email' },
            { label: 'Registered Address', name: 'registeredAddress' },
            { label: 'Business Address', name: 'businessAddress' },
          ],
          required: true,
          span: 2,
        },
      },
      'fundPolicy.salesChargePercent': {
        type: 'number',
        config: {
          label: 'Sales Charges',
          required: true,
          step: 0.01,
          min: 0,
          max: 99999.99,
        },
      },
      salesChargeDisplay: {
        type: 'text',
        config: {
          label: 'Sales Charges Display',
          required: false,
        },
      },
      'fundPolicy.annualManagementFee': {
        type: 'number',
        config: {
          label: 'Annual Management Fee',
          required: true,
          step: 0.01,
          min: 0,
          max: 999.99,
        },
      },
      annualManagementFeeDisplay: {
        type: 'text',
        config: {
          label: 'Annual Management Fee Display',
          required: false,
        },
      },
      'fundPolicy.annualTrusteeFee': {
        type: 'number',
        config: {
          label: 'Trustee Fee',
          required: true,
          step: 0.01,
          min: 0,
          max: 999.99,
        },
      },
      trusteeFeeDisplay: {
        type: 'text',
        config: {
          label: 'Trustee Fee Display',
          required: false,
        },
      },
      switchFee: {
        type: 'number',
        config: {
          label: 'Switch Fee',
          required: true,
          step: 0.01,
          min: 0,
          max: 99999.99,
        },
      },
      switchingFeeDisplay: {
        type: 'text',
        config: {
          label: 'Switching Fee Display',
          required: false,
        },
      },
      collectionBankAccountId: {
        type: 'resource-select',
        config: {
          label: 'Collection Bank Account Name',
          required: true,
          resourceName: resourceNames.collectionBankAccount,
          optionLabel: 'beneficiaryName',
          optionValue: 'id',
          fields: ['id', 'beneficiaryName'],
          extendedFields: [
            { label: 'Bank Account Number', name: 'accountNo' },
            { label: 'Bank Name', name: 'bankName' },
          ],
          span: 2,
        },
      },
      fundSize: {
        type: 'number',
        config: {
          label: 'Fund Size',
          required: true,
          min: 0,
          max: 2147483647,
        },
      },
      fundSizeCurrency: {
        type: 'text',
        config: {
          label: 'Fund Size Currency',
          required: true,
          maxLength: 3,
        },
      },
      fundSizeDate: {
        type: 'date',
        config: {
          label: 'Fund Size Date',
          required: false,
        },
      },
      totalUnitInCirculation: {
        type: 'number',
        config: {
          label: 'Total Unit In Circulation',
          required: true,
          min: 0,
          max: 2147483647,
        },
      },
      totalUnitInCirculationDate: {
        type: 'date',
        config: {
          label: 'Total Unit In Circulation Date',
          required: false,
        },
      },
      annualExpenseRatio: {
        type: 'number',
        config: {
          label: 'Annual Expense Ratio',
          required: true,
          step: 0.01,
          min: 0,
          max: 999.99,
        },
      },
      annualExpenseRatioDate: {
        type: 'date',
        config: {
          label: 'Annual Expense Ratio Date',
          required: false,
        },
      },
      fundDocuments: {
        type: 'custom',
        config: {
          label: '',
          span: 2,
          hideLabel: true,
          required: false,
          rules: {
            validate: (value: any) => {
              if (!value || !Array.isArray(value)) return true;
              
              const urlPattern = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
              
              for (const doc of value) {
                const link = doc.documentLink || '';
                const trimmedLink = link.trim();

                if (trimmedLink !== '' && !urlPattern.test(trimmedLink)) {
                  return `${doc.displayName}: Document link must be a valid HTTP or HTTPS URL (e.g., https://example.com/document.pdf)`;
                }
              }

              return true;
            },
          },
          render: (field) => {
            const { formState, getFieldState } = useFormContext();
            const fieldError = getFieldState(field.name, formState).error;
            const fieldErrorMessage =
              typeof fieldError?.message === 'string' ? fieldError.message : undefined;
            const [documents, setDocuments] = React.useState(() => {
              const allTypes = Object.values(FundDocumentTypes).map((type) => ({
                displayName: type,
                documentLink: '',
              }));

              if (field.value?.length) {
                const result = allTypes.map((defaultDoc) => {
                  const existingDoc = field.value.find(
                    (doc: any) => doc.displayName === defaultDoc.displayName
                  );
                  return existingDoc || defaultDoc;
                });
                return result;
              }

              return allTypes;
            });

            // Track if changes are from user input to prevent circular updates
            const isUserChangeRef = React.useRef(false);

            // Sync state when field.value changes from form load (not from user input)
            React.useEffect(() => {
              if (isUserChangeRef.current) {
                isUserChangeRef.current = false;
                return;
              }

              if (field.value?.length) {
                const allTypes = Object.values(FundDocumentTypes).map((type) => ({
                  displayName: type,
                  documentLink: '',
                }));

                const updatedDocs = allTypes.map((defaultDoc) => {
                  const existingDoc = field.value.find(
                    (doc: any) => doc.displayName === defaultDoc.displayName
                  );
                  return existingDoc || defaultDoc;
                });
                setDocuments(updatedDocs);
              }
            }, [field.value]);

            const handleChange = (displayName: string, value: string) => {
              const updatedDocuments = documents.map((doc) => {
                if (doc.displayName === displayName) {
                  return { ...doc, documentLink: value };
                }
                return doc;
              });
              setDocuments(updatedDocuments);
              isUserChangeRef.current = true;
              field.onChange(updatedDocuments);
            };

            const urlPattern = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
            
            return (
              <div className="w-full flex flex-col gap-4">
                {documents.map((doc: any, index: number) => {
                  const value = doc.documentLink || '';
                  const trimmedValue = value.trim();
                  const submittedErrorMessage = fieldErrorMessage?.startsWith(`${doc.displayName}:`)
                    ? fieldErrorMessage.replace(`${doc.displayName}: `, '')
                    : undefined;

                  // Check if URL format is invalid
                  const isInvalidUrl = trimmedValue !== '' && !urlPattern.test(trimmedValue);
                  
                  return (
                    <div key={`${doc.displayName}-${index}`}>
                      <TextField
                        label={doc.displayName}
                        placeholder="https://example.com/document.pdf"
                        layout="horizontal"
                        value={value}
                        onChange={(e) => handleChange(doc.displayName, e.target.value)}
                        status={isInvalidUrl ? 'danger' : undefined}
                        errorMessage={isInvalidUrl ? 'Document link must be a valid HTTP or HTTPS URL (e.g., https://example.com/document.pdf)' : undefined}
                      />
                    </div>
                  );
                })}
              </div>
            );
          },
        },
      },
    }),
  },
  columns: ({ LinkToDetails }) => [
    {
      id: 'fundName',
      header: 'Fund Name',
      accessorKey: 'fundName',
      cell: (data) => (
        <LinkToDetails resourceId={data.row.original.id} className="font-semibold">
          {data.cell.getValue<string>()}
        </LinkToDetails>
      ),
    },
    {
      id: 'fundCode',
      header: 'Fund Code',
      accessorKey: 'fundCode',
    },
    {
      id: 'isin',
      header: 'ISIN Number',
      accessorKey: 'isin',
    },
    {
      id: 'category',
      header: 'Category',
      accessorKey: 'category',
      cell: (data) => {
        const rawValue = data.cell.getValue<string>();

        const formattedValue = rawValue.split('_').join(' ').toUpperCase();
        return formattedValue;
      },
    },
    {
      id: 'currencyCode',
      header: 'Currency',
      accessorKey: 'currencyCode',
    },
    {
      id: 'navPrice.price',
      header: 'Net Asset Value (NAV)',
      accessorKey: 'navPrice.price',
      cell: (data) => {
        return data.cell.getValue<number>().toLocaleString('en-MY', {
          minimumFractionDigits: 4,
          maximumFractionDigits: 4,
        });
      },
    },
    {
      id: 'navPrice.date',
      header: 'Nav Price Date',
      accessorKey: 'navPrice.date',
      cell: (data) => {
        return formatDate(data.cell.getValue<Date>(), { formatType: 'dateOnly' });
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      cell: (data) => {
        const status = data.cell.getValue<string>();
        return <StatusField status={formatFundInfoStatus(status)} label={status} />;
      },
    },
  ],
  filterControls: {
    fundName: {
      type: 'text',
      config: {
        label: 'Fund Name',
      },
      operator: 'contains',
    },
    fundCode: {
      type: 'text',
      config: {
        label: 'Fund Code',
      },
      operator: 'contains',
    },
    isin: {
      type: 'text',
      config: {
        label: 'ISIN Number',
      },
      operator: 'contains',
    },
    status: {
      type: 'select',
      config: {
        label: 'Status',
        options: [
          { label: 'Active', value: 'Active' },
          { label: 'Inactive', value: 'Inactive' },
        ],
      },
      operator: 'eq',
    },
  },
  defaultSorter: [
    { field: 'createdAt', order: 'desc' },
    { field: 'id', order: 'asc' },
  ],
  show: defineShowPage({
    sections: [
      defineCardSection({
        title: 'Basic Fund Information',
        collapsible: 'expand-by-default',
        fields: [
          'fundName',
          'fundCode',
          'isin',
          'currencyCode',
          { fundPolicy: ['wholeSaleFlag'] },
          'launchDate',
          'launchPrice',
          'category',
          'fundFocusFlag',
          'rm1FundIndicator',
          'localFlag',
          'shariahFlag',
          'esgFlag',
          'manager',
          'status',
        ],
        displays: {
          isin: {
            label: 'ISIN Number',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return data.isin ?? '-';
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={oldValues?.isin ?? '-'}
                  newValue={newValues?.isin ?? '-'}
                />
              );
            },
          },
          launchDate: {
            label: 'Launch Date',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return data.launchDate
                  ? formatDate(data.launchDate, { formatType: 'dateOnly' })
                  : '-';
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={
                    oldValues?.launchDate
                      ? formatDate(oldValues?.launchDate, { formatType: 'dateOnly' })
                      : '-'
                  }
                  newValue={
                    newValues?.launchDate
                      ? formatDate(newValues?.launchDate, { formatType: 'dateOnly' })
                      : '-'
                  }
                />
              );
            },
          },
          'fundPolicy.wholeSaleFlag': {
            label: 'Wholesale Indicator',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return <Switch checked={data.fundPolicy?.wholeSaleFlag} disabled />;
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={oldValues?.wholeSaleFlag === true ? 'Yes' : 'No'}
                  newValue={newValues?.wholeSaleFlag === true ? 'Yes' : 'No'}
                />
              );
            },
          },
          currencyCode: {
            label: 'Currency',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return data.currencyCode;
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={oldValues?.currencyCode}
                  newValue={newValues?.currencyCode}
                />
              );
            },
          },
          category: {
            label: 'Fund Category',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return sentenceCase(data.category);
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={sentenceCase(oldValues?.category)}
                  newValue={sentenceCase(newValues?.category)}
                />
              );
            },
          },
          localFlag: {
            label: 'Geographical Location',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return data.localFlag ? 'Local' : 'Global';
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={oldValues?.localFlag ? 'Local' : 'Global'}
                  newValue={newValues?.localFlag ? 'Local' : 'Global'}
                />
              );
            },
          },
          fundFocusFlag: {
            label: 'Fund Focus Indicator',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return <Switch checked={data.fundFocusFlag} disabled />;
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={oldValues?.fundFocusFlag === true ? 'Yes' : 'No'}
                  newValue={newValues?.fundFocusFlag === true ? 'Yes' : 'No'}
                />
              );
            },
          },
          rm1FundIndicator: {
            label: 'RM1 Fund Indicator',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return <Switch checked={data.rm1FundIndicator} disabled />;
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={oldValues?.rm1FundIndicator === true ? 'Yes' : 'No'}
                  newValue={newValues?.rm1FundIndicator === true ? 'Yes' : 'No'}
                />
              );
            },
          },
          shariahFlag: {
            label: 'Shariah',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return <Switch checked={data.shariahFlag} disabled />;
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={oldValues?.shariahFlag === true ? 'Yes' : 'No'}
                  newValue={newValues?.shariahFlag === true ? 'Yes' : 'No'}
                />
              );
            },
          },
          esgFlag: {
            label: 'ESG Flag',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return <Switch checked={data.esgFlag} disabled />;
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={oldValues?.esgFlag === true ? 'Yes' : 'No'}
                  newValue={newValues?.esgFlag === true ? 'Yes' : 'No'}
                />
              );
            },
          },
          manager: {
            label: 'Manager',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return formatFundManager(data.manager ?? null);
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={formatFundManager(oldValues?.manager ?? null)}
                  newValue={formatFundManager(newValues?.manager ?? null)}
                />
              );
            },
          },
        },
      }),
      defineCardSection({
        title: 'Investment Requirement',
        collapsible: 'expand-by-default',
        fields: [
          {
            fundPolicy: [
              'minInitInvestment',
              'minSubsequentInvestment',
              'minRedemptionUnit',
              'minHoldingUnit',
              'minSwitchingUnit',
            ],
          },
        ],
        displays: {
          'fundPolicy.minInitInvestment': {
            label: 'Minimum Initial Investment',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return Number(data.fundPolicy?.minInitInvestment)?.toLocaleString('en-MY');
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={Number(oldValues?.minInitInvestment)?.toLocaleString('en-MY')}
                  newValue={Number(newValues?.minInitInvestment)?.toLocaleString('en-MY')}
                />
              );
            },
          },
          'fundPolicy.minSubsequentInvestment': {
            label: 'Minimum Subsequent Investment',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return Number(data.fundPolicy?.minSubsequentInvestment)?.toLocaleString('en-MY');
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={Number(oldValues?.minSubsequentInvestment)?.toLocaleString('en-MY')}
                  newValue={Number(newValues?.minSubsequentInvestment)?.toLocaleString('en-MY')}
                />
              );
            },
          },
          'fundPolicy.minRedemptionUnit': {
            label: 'Minimum Redemption Unit',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return Number(data.fundPolicy?.minRedemptionUnit)?.toLocaleString('en-MY');
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={Number(oldValues?.minRedemptionUnit)?.toLocaleString('en-MY')}
                  newValue={Number(newValues?.minRedemptionUnit)?.toLocaleString('en-MY')}
                />
              );
            },
          },
          'fundPolicy.minHoldingUnit': {
            label: 'Minimum Holding Unit',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return Number(data.fundPolicy?.minHoldingUnit)?.toLocaleString('en-MY');
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={Number(oldValues?.minHoldingUnit)?.toLocaleString('en-MY')}
                  newValue={Number(newValues?.minHoldingUnit)?.toLocaleString('en-MY')}
                />
              );
            },
          },
          'fundPolicy.minSwitchingUnit': {
            label: 'Minimum Switching Unit',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return Number(data.fundPolicy?.minSwitchingUnit)?.toLocaleString('en-MY');
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={Number(oldValues?.minSwitchingUnit)?.toLocaleString('en-MY')}
                  newValue={Number(newValues?.minSwitchingUnit)?.toLocaleString('en-MY')}
                />
              );
            },
          },
        },
      }),
      defineCardSection({
        title: 'Operational Details',
        collapsible: 'expand-by-default',
        fields: [
          { fundPolicy: ['cutOffTime', 'redemptionCutOffTime'] },
          'financialYearEnd',
          'distributionPolicy',
          'pricingBasic',
        ],
        displays: {
          'fundPolicy.cutOffTime': {
            label: 'Cut Off Time',
            render: (data, modifications) => {
              if (!modifications) {
                return data.fundPolicy?.cutOffTime;
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={oldValues?.cutOffTime}
                  newValue={newValues?.cutOffTime}
                />
              );
            },
          },
          'fundPolicy.redemptionCutOffTime': {
            label: 'Redemption Cut Off Time',
            render: (data, modifications) => {
              if (!modifications) {
                return data.fundPolicy?.redemptionCutOffTime;
              }
              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={oldValues?.redemptionCutOffTime}
                  newValue={newValues?.redemptionCutOffTime}
                />
              );
            },
          },
          distributionPolicy: {
            label: 'Distribution Type / Policy',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return data.distributionPolicy;
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={oldValues?.distributionPolicy}
                  newValue={newValues?.distributionPolicy}
                />
              );
            },
          },
          pricingBasic: {
            label: 'Pricing Basic',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return data.pricingBasic;
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={oldValues?.pricingBasic}
                  newValue={newValues?.pricingBasic}
                />
              );
            },
          },
        },
      }),
      defineCardSection({
        title: 'Risk and Performance',
        collapsible: 'expand-by-default',
        fields: ['fundRiskProfile', 'benchmark', 'lipperMyClass', 'assetAllocation'],
        displays: {
          lipperMyClass: {
            label: 'Lipper Malaysian Classification',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return data.lipperMyClass;
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={oldValues?.lipperMyClass}
                  newValue={newValues?.lipperMyClass}
                />
              );
            },
          },
          fundRiskProfile: {
            label: 'Fund Risk Profile',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return formatFundRiskProfile(data.fundRiskProfile);
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={formatFundRiskProfile(oldValues?.fundRiskProfile)}
                  newValue={formatFundRiskProfile(newValues?.fundRiskProfile)}
                />
              );
            },
          },
        },
      }),
      defineCardSection({
        title: 'Objective and Policies',
        collapsible: 'expand-by-default',
        fields: ['investmentObj'],
        displays: {
          investmentObj: {
            label: 'Investment Objective',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return data.investmentObj;
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={oldValues?.investmentObj}
                  newValue={newValues?.investmentObj}
                />
              );
            },
          },
        },
      }),
      defineCardSection({
        title: 'Trustee Information',
        collapsible: 'expand-by-default',
        fields: [
          {
            trustee: [
              'name',
              'contactNo',
              'faxNo',
              'website',
              'email',
              'registeredAddress',
              'businessAddress',
            ],
          },
        ],
        displays: {
          'trustee.name': {
            label: 'Trustee Name',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return data.trustee?.name;
              }

              return (
                <modifications.ModificationDiff
                  oldValue={modifications.oldValues.name}
                  newValue={modifications.newValues.name}
                  getModifiedResource={{
                    resourceName: resourceNames.trustee,
                    oldId: modifications.oldValues.name,
                    newId: modifications.newValues.name,
                    field: 'name',
                  }}
                />
              );
            },
          },
          'trustee.contactNo': {
            label: 'Trustee Contact Number',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return data.trustee?.contactNo;
              }

              return (
                <modifications.ModificationDiff
                  oldValue={modifications.oldValues.contactNo}
                  newValue={modifications.newValues.contactNo}
                  getModifiedResource={{
                    resourceName: resourceNames.trustee,
                    oldId: modifications.oldValues.contactNo,
                    newId: modifications.newValues.contactNo,
                    field: 'contactNo',
                  }}
                />
              );
            },
          },
          'trustee.faxNo': {
            label: 'Trustee Fax Number',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return data.trustee?.faxNo;
              }

              return (
                <modifications.ModificationDiff
                  oldValue={modifications.oldValues.faxNo}
                  newValue={modifications.newValues.faxNo}
                  getModifiedResource={{
                    resourceName: resourceNames.trustee,
                    oldId: modifications.oldValues.faxNo,
                    newId: modifications.newValues.faxNo,
                    field: 'faxNo',
                  }}
                />
              );
            },
          },
          'trustee.website': {
            label: 'Trustee Website',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return data.trustee?.website;
              }

              return (
                <modifications.ModificationDiff
                  oldValue={modifications.oldValues.website}
                  newValue={modifications.newValues.website}
                  getModifiedResource={{
                    resourceName: resourceNames.trustee,
                    oldId: modifications.oldValues.website,
                    newId: modifications.newValues.website,
                    field: 'website',
                  }}
                />
              );
            },
          },
          'trustee.email': {
            label: 'Trustee Email',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return data.trustee?.email;
              }

              return (
                <modifications.ModificationDiff
                  oldValue={modifications.oldValues.email}
                  newValue={modifications.newValues.email}
                  getModifiedResource={{
                    resourceName: resourceNames.trustee,
                    oldId: modifications.oldValues.email,
                    newId: modifications.newValues.email,
                    field: 'email',
                  }}
                />
              );
            },
          },
          'trustee.registeredAddress': {
            label: 'Trustee Registered Address',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return data.trustee?.registeredAddress;
              }

              return (
                <modifications.ModificationDiff
                  oldValue={modifications.oldValues.registeredAddress}
                  newValue={modifications.newValues.registeredAddress}
                  getModifiedResource={{
                    resourceName: resourceNames.trustee,
                    oldId: modifications.oldValues.registeredAddress,
                    newId: modifications.newValues.registeredAddress,
                    field: 'registeredAddress',
                  }}
                />
              );
            },
          },
          'trustee.businessAddress': {
            label: 'Trustee Business Address',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return data.trustee?.businessAddress;
              }

              return (
                <modifications.ModificationDiff
                  oldValue={modifications.oldValues.businessAddress}
                  newValue={modifications.newValues.businessAddress}
                  getModifiedResource={{
                    resourceName: resourceNames.trustee,
                    oldId: modifications.oldValues.businessAddress,
                    newId: modifications.newValues.businessAddress,
                    field: 'businessAddress',
                  }}
                />
              );
            },
          },
        },
      }),
      defineCardSection({
        title: 'Fees and Charges',
        collapsible: 'expand-by-default',
        fields: [
          'salesChargeDisplay',
          'switchingFeeDisplay',
          'annualManagementFeeDisplay',
          'trusteeFeeDisplay',
          {
            fundPolicy: ['salesChargePercent', 'annualManagementFee', 'annualTrusteeFee'],
          },
          {
            collectionBankAccount: ['beneficiaryName', 'accountNo'],
          },
        ],
        displays: {
          'fundPolicy.salesChargePercent': {
            label: 'Sales Charges',
            render: (data, modifications) => {
              if (!modifications) {
                const salesChargePercent = data.fundPolicy?.salesChargePercent;
                if (salesChargePercent == null || salesChargePercent === '') {
                  return 'Nil';
                }
                return `Up to ${Number(salesChargePercent).toLocaleString('en-MY', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}%`;
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={`Up to ${Number(oldValues?.salesChargePercent).toLocaleString('en-MY', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}%`}
                  newValue={`Up to ${Number(newValues?.salesChargePercent).toLocaleString('en-MY', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}%`}
                />
              );
            },
          },
          'fundPolicy.annualManagementFee': {
            label: 'Annual Management Fee',
            render: (data, modifications) => {
              if (!modifications) {
                const annualManagementFee = data.fundPolicy?.annualManagementFee;
                if (annualManagementFee == null || annualManagementFee === '') {
                  return 'Nil';
                }
                return `${Number(annualManagementFee).toLocaleString('en-MY', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}%`;
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={`${Number(oldValues?.annualManagementFee).toLocaleString('en-MY', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}%`}
                  newValue={`${Number(newValues?.annualManagementFee).toLocaleString('en-MY', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}%`}
                />
              );
            },
          },
          'fundPolicy.annualTrusteeFee': {
            label: 'Trustee Fee',
            render: (data, modifications) => {
              if (!modifications) {
                const annualTrusteeFee = data.fundPolicy?.annualTrusteeFee;
                if (annualTrusteeFee == null || annualTrusteeFee === '') {
                  return 'Nil';
                }
                return `${Number(annualTrusteeFee).toLocaleString('en-MY', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}%`;
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={`${Number(oldValues?.annualTrusteeFee).toLocaleString('en-MY', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}%`}
                  newValue={`${Number(newValues?.annualTrusteeFee).toLocaleString('en-MY', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}%`}
                />
              );
            },
          },
          switchFee: {
            label: 'Switch Fee',
            render: (data: Fund, modifications) => {
              const switchFee = data.switchFee;
              if (!modifications && (switchFee == null || switchFee === '')) {
                return 'Nil';
              }

              if (!modifications) {
                return Number(switchFee).toLocaleString('en-MY', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={Number(oldValues?.switchFee).toLocaleString('en-MY', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  newValue={Number(newValues?.switchFee).toLocaleString('en-MY', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                />
              );
            },
          },
          'collectionBankAccount.beneficiaryName': {
            label: 'Collection Bank Account Name',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return data.collectionBankAccount?.beneficiaryName;
              }

              return (
                <modifications.ModificationDiff
                  oldValue={modifications.oldValues.beneficiaryName}
                  newValue={modifications.newValues.beneficiaryName}
                  getModifiedResource={{
                    resourceName: resourceNames.collectionBankAccount,
                    oldId: modifications.oldValues.beneficiaryName,
                    newId: modifications.newValues.beneficiaryName,
                    field: 'beneficiaryName',
                  }}
                />
              );
            },
          },
          'collectionBankAccount.accountNo': {
            label: 'Collection Bank Account Number',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return data.collectionBankAccount?.accountNo;
              }

              return (
                <modifications.ModificationDiff
                  oldValue={modifications.oldValues.accountNo}
                  newValue={modifications.newValues.accountNo}
                  getModifiedResource={{
                    resourceName: resourceNames.collectionBankAccount,
                    oldId: modifications.oldValues.accountNo,
                    newId: modifications.newValues.accountNo,
                    field: 'accountNo',
                  }}
                />
              );
            },
          },
        },
      }),
      defineCardSection({
        title: 'Others',
        collapsible: 'expand-by-default',
        fields: [
          {
            navPrice: ['price', 'date'],
          },
          'fundSize',
          'fundSizeCurrency',
          'fundSizeDate',
          'totalUnitInCirculation',
          'totalUnitInCirculationDate',
          'annualExpenseRatio',
          'annualExpenseRatioDate',
        ],
        displays: {
          navPrice: {
            label: 'Net Asset Value (NAV)',
            render: (data) => {
              return data.navPrice?.price?.toLocaleString('en-MY', {
                minimumFractionDigits: 4,
                maximumFractionDigits: 4,
              });
            },
          },
          fundSize: {
            label: 'Fund Size',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return Number(data.fundSize)?.toLocaleString('en-MY', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={Number(oldValues?.fundSize).toLocaleString('en-MY', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  newValue={Number(newValues?.fundSize).toLocaleString('en-MY', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                />
              );
            },
          },
          totalUnitInCirculation: {
            label: 'Total Unit In Circulation',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return Number(data.totalUnitInCirculation)?.toLocaleString('en-MY');
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={Number(oldValues?.totalUnitInCirculation).toLocaleString('en-MY')}
                  newValue={Number(newValues?.totalUnitInCirculation).toLocaleString('en-MY')}
                />
              );
            },
          },
          totalUnitInCirculationDate: {
            label: 'Total Unit In Circulation Date',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return data.totalUnitInCirculationDate
                  ? formatDate(data.totalUnitInCirculationDate, { formatType: 'dateOnly' })
                  : '-';
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={
                    oldValues?.totalUnitInCirculationDate
                      ? formatDate(oldValues?.totalUnitInCirculationDate, {
                          formatType: 'dateOnly',
                        })
                      : '-'
                  }
                  newValue={
                    newValues?.totalUnitInCirculationDate
                      ? formatDate(newValues?.totalUnitInCirculationDate, {
                          formatType: 'dateOnly',
                        })
                      : '-'
                  }
                />
              );
            },
          },
          annualExpenseRatioDate: {
            label: 'Annual Expense Ratio Date',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return data.annualExpenseRatioDate
                  ? formatDate(data.annualExpenseRatioDate, { formatType: 'dateOnly' })
                  : '-';
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={
                    oldValues?.annualExpenseRatioDate
                      ? formatDate(oldValues?.annualExpenseRatioDate, {
                          formatType: 'dateOnly',
                        })
                      : '-'
                  }
                  newValue={
                    newValues?.annualExpenseRatioDate
                      ? formatDate(newValues?.annualExpenseRatioDate, {
                          formatType: 'dateOnly',
                        })
                      : '-'
                  }
                />
              );
            },
          },
          fundSizeDate: {
            label: 'Fund Size Date',
            render: (data: Fund, modifications) => {
              if (!modifications) {
                return data.fundSizeDate
                  ? formatDate(data.fundSizeDate, { formatType: 'dateOnly' })
                  : '-';
              }

              const { oldValues, newValues, ModificationDiff } = modifications;
              return (
                <ModificationDiff
                  oldValue={
                    oldValues?.fundSizeDate
                      ? formatDate(oldValues?.fundSizeDate, { formatType: 'dateOnly' })
                      : '-'
                  }
                  newValue={
                    newValues?.fundSizeDate
                      ? formatDate(newValues?.fundSizeDate, { formatType: 'dateOnly' })
                      : '-'
                  }
                />
              );
            },
          },
        },
      }),
      defineCardSection({
        title: 'Fund Documents',
        collapsible: 'expand-by-default',
        fields: [{ fundDocuments: ['id', 'displayName', 'documentLink'] }],
        displays: {
          fundDocuments: {
            label: ' ',
            span: 2,
            render: (data: Fund, modifications?: any) => {
              const allTypes = Object.values(FundDocumentTypes);
              const existingDocs = data.fundDocuments ?? [];
              const updateMany = modifications?.newValues?.fundDocuments?.updateMany ?? [];
              const createMany = modifications?.newValues?.fundDocuments?.createMany?.data ?? [];

              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
                  {allTypes.map((type) => {
                    const existingDoc = existingDocs.find((doc) => doc.displayName === type);
                    const updateMod = existingDoc
                      ? updateMany.find((doc: any) => doc.where.id === existingDoc.id)
                      : null;
                    const createMod = createMany.find((doc: any) => doc.displayName === type);
                    const oldValue = existingDoc?.documentLink || '-';
                    const newValue = updateMod?.data?.documentLink ?? createMod?.documentLink;
                    const hasChanges = Boolean(modifications && (updateMod || createMod));
                    return (
                      <div key={type}>
                        <div
                          style={{
                            fontWeight: 500,
                            fontSize: '14px',
                            marginBottom: '4px',
                            color: hasChanges ? '#ef4444' : '#000000',
                          }}
                        >
                          {type}
                        </div>
                        <div style={{ fontSize: '14px', wordBreak: 'break-word' }}>
                          {hasChanges ? (
                            <modifications.ModificationDiff oldValue={oldValue} newValue={newValue || '-'} />
                          ) : (
                            <span style={{ color: '#000000' }}>{oldValue}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            },
          },
        },
      }),
    ],
  }),
});
